import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  List,
  ListItemText,
  ListItemAvatar,
  Divider,
  useTheme,
  alpha,
  Tab,
} from '@material-ui/core'
import {
  CloudUpload,
  Storage,
  Assessment,
  FileCopy,
  CheckCircle,
  Schedule,
  Business,
  Refresh,
  BarChart,
  Timeline,
} from '@material-ui/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { getFilesDashboardData } from '../../api/file/filesDashboard'
import UploadTrendsChart from './components/UploadTrendsChart'
import {
  DashboardContainer,
  DashboardContent,
  StyledCard,
  StyledCardContent,
  ContentScrollArea,
  ScrollableList,
  DashboardHeader,
  DashboardTitle,
  DashboardSubtitle,
  StyledTabs,
  StatCard,
  StatLabel,
  StatValue,
  ProgressContainer,
  LoadingWrapper,
  RecentUploadItem,
} from './styles/styles'

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 0.5 }}>{children}</Box>}
    </div>
  )
}

const FilesDashboard = () => {
  const theme = useTheme()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedDays, setSelectedDays] = useState(30)
  const [tabValue, setTabValue] = useState(0)

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getFilesDashboardData(selectedDays, { refresh: false })
      setDashboardData(data)
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
  }, [selectedDays])

  const handleRefresh = () => {
    fetchDashboardData()
  }

  const handleDaysChange = (event) => {
    setSelectedDays(event.target.value)
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const getFileTypeColor = (fileType) => {
    const colors = {
      fastq: theme.palette.primary.main,
      vcf: theme.palette.secondary.main,
      gene_panel: theme.palette.warning.main,
    }
    return colors[fileType] || theme.palette.grey[500]
  }

  const getFileTypeIcon = (fileType) => {
    const icons = {
      fastq: <FileCopy />,
      vcf: <Assessment />,
      gene_panel: <Business />,
    }
    return icons[fileType] || <FileCopy />
  }

  const formatStorageSize = (sizeInKB) => {
    const sizeInMB = sizeInKB / 1024
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(1)} GB`
    }
    return `${sizeInMB.toFixed(1)} MB`
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading && !dashboardData) {
    return (
      <StyledCard>
        <StyledCardContent>
          <LoadingWrapper>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                <Assessment sx={{ fontSize: 32, color: theme.palette.primary.main }} />
              </motion.div>
              <Typography variant="body2" color="textSecondary">
                Loading dashboard data...
              </Typography>
            </Box>
          </LoadingWrapper>
        </StyledCardContent>
      </StyledCard>
    )
  }

  if (error) {
    return (
      <StyledCard>
        <StyledCardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
            <Typography variant="h6" color="error">
              Error loading dashboard
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              {error}
            </Typography>
            <IconButton onClick={handleRefresh} color="primary">
              <Refresh />
            </IconButton>
          </Box>
        </StyledCardContent>
      </StyledCard>
    )
  }

  if (!dashboardData) {
    return null
  }

  const { file_type_distribution, recent_uploads, analysis_status, organization_stats, upload_trends } = dashboardData

  return (
    <DashboardContainer
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <DashboardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <AnimatePresence>
          {loading && (
            <LoadingWrapper>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                  <Assessment sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                </motion.div>
                <Typography variant="body2" color="textSecondary">
                  Refreshing data...
                </Typography>
              </Box>
            </LoadingWrapper>
          )}
        </AnimatePresence>

        {/* Dashboard Header */}
        <DashboardHeader>
          {/* First Row: Title and Description */}
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', mb: 1 }}
          >
            <Box>
              <DashboardTitle>File statistics</DashboardTitle>
              <DashboardSubtitle>Uploads, analyses and storage for your organization</DashboardSubtitle>
            </Box>
          </Box>

          {/* Second Row: Tab Navigation and Controls */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '4px 0',
              borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.05)}`,
              marginTop: '4px',
            }}
          >
            {/* Tab Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <StyledTabs value={tabValue} onChange={handleTabChange}>
                <Tab
                  label="Overview"
                  icon={<BarChart sx={{ fontSize: '0.9rem' }} />}
                  sx={{ minHeight: '40px', padding: '8px 16px', fontSize: '0.8rem' }}
                />
                <Tab
                  label="Uploads"
                  icon={<CloudUpload sx={{ fontSize: '0.9rem' }} />}
                  sx={{ minHeight: '40px', padding: '8px 16px', fontSize: '0.8rem' }}
                />
                <Tab
                  label="Analysis"
                  icon={<Timeline sx={{ fontSize: '0.9rem' }} />}
                  sx={{ minHeight: '40px', padding: '8px 16px', fontSize: '0.8rem' }}
                />
              </StyledTabs>
            </Box>

            {/* Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Enhanced Time Range Selection */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase',
                  }}
                >
                  Time Range
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={selectedDays}
                    onChange={handleDaysChange}
                    sx={{
                      height: '32px',
                      fontSize: '0.8rem',
                      '& .MuiSelect-select': {
                        padding: '6px 12px',
                        display: 'flex',
                        alignItems: 'center',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider',
                        borderWidth: '1px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    }}
                  >
                    <MenuItem value={7} sx={{ fontSize: '0.8rem' }}>
                      Last 7 days
                    </MenuItem>
                    <MenuItem value={30} sx={{ fontSize: '0.8rem' }}>
                      Last 30 days
                    </MenuItem>
                    <MenuItem value={90} sx={{ fontSize: '0.8rem' }}>
                      Last 90 days
                    </MenuItem>
                    <MenuItem value={365} sx={{ fontSize: '0.8rem' }}>
                      Last year
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Basic Refresh Button */}
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  size="small"
                  sx={{
                    width: '32px',
                    height: '32px',
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: 'action.hover',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Refresh sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DashboardHeader>

        {/* Tab Panels */}
        <ContentScrollArea>
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={1} sx={{ mb: 1.5, mt: -0.5 }}>
              {/* Uploads in Period */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: alpha(theme.palette.success.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.success.main,
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      <CloudUpload />
                    </Box>
                    <StatValue>{upload_trends.total_uploads_in_period}</StatValue>
                  </Box>
                  <StatLabel>Uploads ({selectedDays}d)</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    {upload_trends.average_daily_uploads.toFixed(1)} avg/day
                  </Typography>
                </StatCard>
              </Grid>

              {/* Analysis Completion */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: alpha(theme.palette.info.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.info.main,
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      <CheckCircle />
                    </Box>
                    <StatValue>{analysis_status.completion_rate.toFixed(1)}%</StatValue>
                  </Box>
                  <StatLabel>Analysis Rate</StatLabel>
                  <ProgressContainer>
                    <LinearProgress
                      variant="determinate"
                      value={analysis_status.completion_rate}
                      sx={{ height: 4, borderRadius: 2, marginTop: theme.spacing(0.5) }}
                    />
                  </ProgressContainer>
                </StatCard>
              </Grid>

              {/* Current Balance */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: alpha(theme.palette.primary.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main,
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      <Business />
                    </Box>
                    <StatValue>{organization_stats.current_balance}</StatValue>
                  </Box>
                  <StatLabel>Current Balance</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    Organization balance
                  </Typography>
                </StatCard>
              </Grid>

              {/* Total Completed Analysis Success */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: alpha(theme.palette.success.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.success.main,
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      <Assessment />
                    </Box>
                    <StatValue>{analysis_status.breakdown?.success || 0}</StatValue>
                  </Box>
                  <StatLabel>Analysis Success</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    Successfully completed
                  </Typography>
                </StatCard>
              </Grid>
            </Grid>

            {/* Divider */}
            <Divider sx={{ my: 1.5, borderColor: 'divider' }} />

            {/* Additional Metrics */}
            <Grid container spacing={1} sx={{ mb: 1.5 }}>
              {/* Total Files */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: alpha(theme.palette.primary.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main,
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      <FileCopy />
                    </Box>
                    <StatValue>{file_type_distribution.total_files}</StatValue>
                  </Box>
                  <StatLabel>Total Files</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    Across all file types
                  </Typography>
                </StatCard>
              </Grid>

              {/* Storage Usage */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: alpha(theme.palette.warning.main, 0.12),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.warning.main,
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      <Storage />
                    </Box>
                    <StatValue>{formatStorageSize(organization_stats.organisation_storage_usage)}</StatValue>
                  </Box>
                  <StatLabel>Storage Used</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    Organization storage
                  </Typography>
                </StatCard>
              </Grid>

              {/* FASTQ Files */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: `linear-gradient(135deg, 
                        ${alpha(getFileTypeColor('fastq'), 0.1)} 0%, 
                        ${alpha(getFileTypeColor('fastq'), 0.05)} 100%
                      )`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: getFileTypeColor('fastq'),
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      {getFileTypeIcon('fastq')}
                    </Box>
                    <StatValue sx={{ color: getFileTypeColor('fastq') }}>
                      {file_type_distribution.fastq_files}
                    </StatValue>
                  </Box>
                  <StatLabel>FASTQ Files</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    {file_type_distribution.distribution_percentage?.fastq?.toFixed(1) || 0}% of total
                  </Typography>
                </StatCard>
              </Grid>

              {/* VCF Files */}
              <Grid item xs={12} sm={6} lg={3}>
                <StatCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: theme.spacing(0.5) }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        background: `linear-gradient(135deg, 
                        ${alpha(getFileTypeColor('vcf'), 0.1)} 0%, 
                        ${alpha(getFileTypeColor('vcf'), 0.05)} 100%
                      )`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: getFileTypeColor('vcf'),
                        marginRight: theme.spacing(1),
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      {getFileTypeIcon('vcf')}
                    </Box>
                    <StatValue sx={{ color: getFileTypeColor('vcf') }}>{file_type_distribution.vcf_files}</StatValue>
                  </Box>
                  <StatLabel>VCF Files</StatLabel>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: '0.75rem', marginTop: theme.spacing(0.25) }}
                  >
                    {file_type_distribution.distribution_percentage?.vcf?.toFixed(1) || 0}% of total
                  </Typography>
                </StatCard>
              </Grid>
            </Grid>

            {/* Recent Upload Log */}
            <Box sx={{ mt: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  mb: 1,
                  textTransform: 'uppercase',
                }}
              >
                Recent Upload
              </Typography>
              {recent_uploads && recent_uploads.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: '8px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  }}
                >
                  <CloudUpload sx={{ fontSize: '1rem', color: 'success.main', mr: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {recent_uploads[0].file_name} | by {recent_uploads[0].uploader} on{' '}
                      {formatDate(recent_uploads[0].upload_time)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: '8px',
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    No recent uploads
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={2}>
              {/* Upload Trends Chart - Fixed height */}
              <Grid item xs={12} lg={7}>
                <Card
                  sx={{
                    height: '350px',
                    display: 'flex',
                    flexDirection: 'column',
                    background: `linear-gradient(135deg, 
                      ${alpha(theme.palette.background.paper, 0.8)} 0%, 
                      ${alpha(theme.palette.action.hover, 0.3)} 100%
                    )`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                      <UploadTrendsChart data={upload_trends} height={320} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Recent Uploads - Show all data */}
              <Grid item xs={12} lg={5}>
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    background: `linear-gradient(135deg, 
                      ${alpha(theme.palette.background.paper, 0.8)} 0%, 
                      ${alpha(theme.palette.action.hover, 0.3)} 100%
                    )`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    height: '350px',
                  }}
                >
                  <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'text.primary',
                      }}
                    >
                      Recent Uploads ({recent_uploads.length})
                    </Typography>
                    <ScrollableList sx={{ flex: 1, mt: 1 }}>
                      {recent_uploads.map((upload, index) => (
                        <React.Fragment key={index}>
                          <RecentUploadItem>
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  bgcolor: getFileTypeColor(upload.operation_type.toLowerCase().replace('upload_', '')),
                                  width: 36,
                                  height: 36,
                                }}
                              >
                                {getFileTypeIcon(upload.operation_type.toLowerCase().replace('upload_', ''))}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                  {upload.file_name}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="textSecondary"
                                    sx={{ fontSize: '0.75rem' }}
                                  >
                                    {upload.uploader}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="textSecondary"
                                    sx={{ mb: 0.5, fontSize: '0.75rem' }}
                                  >
                                    {formatDate(upload.upload_time)}
                                  </Typography>
                                  <Chip
                                    label={upload.operation_type.replace('UPLOAD_', '').replace('_', ' ')}
                                    size="small"
                                    sx={{
                                      fontSize: '0.75rem',
                                      height: 22,
                                      '& .MuiChip-label': { px: 1 },
                                    }}
                                  />
                                </Box>
                              }
                            />
                          </RecentUploadItem>
                          {index < recent_uploads.length - 1 && <Divider sx={{ my: 0.5 }} />}
                        </React.Fragment>
                      ))}
                    </ScrollableList>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box>
              <Grid container spacing={1.5}>
                {/* Analysis Completion Rate */}
                <Grid item xs={12} sm={6} lg={4}>
                  <StatCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircle color="success" />
                      <StatLabel>Completion Rate</StatLabel>
                    </Box>
                    <StatValue sx={{ color: 'success.main' }}>{analysis_status.completion_rate.toFixed(1)}%</StatValue>
                    <ProgressContainer>
                      <LinearProgress
                        variant="determinate"
                        value={analysis_status.completion_rate}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </ProgressContainer>
                  </StatCard>
                </Grid>

                {/* Total Analyses */}
                <Grid item xs={12} sm={6} lg={4}>
                  <StatCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Assessment color="primary" />
                      <StatLabel>Total Analyses</StatLabel>
                    </Box>
                    <StatValue>{analysis_status.total_analyses}</StatValue>
                    <Typography variant="caption" color="textSecondary">
                      Files in queue
                    </Typography>
                  </StatCard>
                </Grid>

                {/* Completed Analyses */}
                <Grid item xs={12} sm={6} lg={4}>
                  <StatCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckCircle color="success" />
                      <StatLabel>Completed</StatLabel>
                    </Box>
                    <StatValue sx={{ color: 'success.main' }}>{analysis_status.completed_analyses}</StatValue>
                    <Typography variant="caption" color="textSecondary">
                      Successfully processed
                    </Typography>
                  </StatCard>
                </Grid>

                {/* Pending Analyses */}
                <Grid item xs={12} sm={6} lg={4}>
                  <StatCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Schedule color="warning" />
                      <StatLabel>Pending</StatLabel>
                    </Box>
                    <StatValue sx={{ color: 'warning.main' }}>{analysis_status.pending_analyses}</StatValue>
                    <Typography variant="caption" color="textSecondary">
                      Waiting in queue
                    </Typography>
                  </StatCard>
                </Grid>

                {/* Failed Analyses */}
                <Grid item xs={12} sm={6} lg={4}>
                  <StatCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Assessment color="error" />
                      <StatLabel>Failed</StatLabel>
                    </Box>
                    <StatValue sx={{ color: 'error.main' }}>{analysis_status.failed_analyses || 0}</StatValue>
                    <Typography variant="caption" color="textSecondary">
                      Analysis errors
                    </Typography>
                  </StatCard>
                </Grid>

                {/* Analysis Queue Status */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                      Queue Status
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Files in Progress
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {analysis_status.pending_analyses}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Successfully Completed
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {analysis_status.completed_analyses}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="textSecondary">
                        Overall Completion Rate
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        {analysis_status.completion_rate.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </ContentScrollArea>
      </DashboardContent>
    </DashboardContainer>
  )
}

export default FilesDashboard
